/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { inject } from "mobx-react";
import { PageCanvas } from "components/document/page/PageCanvas";
import { getPageLoadStatus } from "selectors/pageSelectors";
import { getIsTileCanvasLoaded } from "selectors/pagedViewSelectors";
import { pageContentHandler } from "pageContentHandler";
import { getPageOverlays, getOverlayUpdatedTimestamp } from "selectors/overlayDropinSelectors";
import { overlayEvents } from "overlays/OverlayEvents";
import {
  isTouchSelectionInProgress,
  showGrabbers,
  getEventOnGrabber,
  isThereAnySelection
} from "dc-preview-utils/src/selectors/touchSelectors";
import { getPageQuadsArray } from "dc-preview-utils/src/selectors/pageHighlightsSelectors";
import {
  getPageHeight,
  getPageWidth,
  getPageOrigHeight,
  getPageOrigWidth,
  getPageRotation,
  getPage,
  getPageBoxes,
  getPageTiles,
  getPageAccText,
  isPageTextLoadFailed
} from "selectors/pageSelectors";
import { getPageZoomLevel } from "selectors/zoom";
import { contextMenuManager } from "contextMenuManager";
import { isInitialPageLoaded } from "selectors/pagedViewState";
import { isIE, isEdge } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { isTouchEvent } from "dc-preview-utils/src/selectors/touchSelectors";
import { getViewRotationAngle, isFitOneFullPageMode, getLatestHitTestResponse } from "selectors/documentSelectors";
import { getCursorStateChange, getCurrentCursor } from "selectors/overlayDropinSelectors";
import {
  isTextSelectionEnabled,
  isPanActive,
  getShouldUseModernViewer,
  getShouldShowInteractiveTooltips,
  getShouldUseMobileModernViewer,
  getIsMultiDocModeEnabled
} from "selectors/appSelectors";
import { persistEvent, getCoordinatesRelativeToPage } from "utils/EventHandlingUtils";
import { triggerPageMouseLeaveEvent, triggerPageMouseEnterEvent } from "publicApis/Events/events";
import { getCurrentPageNumber } from "selectors/pageNumber";
import { getHyperlinkUrl } from "utils/UrlUtils";
import { contextMenuTriggerType } from "dc-preview-utils/src/Constants/enums";
import { isReadAloudActive, isReadAloudPaused } from "selectors/readAloudSelectors";
import { isHighContrastEnabled } from "selectors/highContrastSelectors";
import { getCursorPageIndex, getIsCursorVisible } from "dc-preview-utils/src/selectors/cursorSelectors";
import { getCaretMode } from "selectors/caretModeSelectors";
import { FORM_ACCESSIBILITY_NODES_PREFIX } from "constants/constants";
import { withTheme } from "context/themeContext";
import { shouldFetchAccContent } from "selectors/accessibilitySelectors";
import { pageContentTypes } from "pageContentHandler/HitTester";
import { transformViewToPDF } from "dc-preview-utils/src/Utils/PageUtils";

function mapStoreToProps(stores, ownProps) {
  const pageTiles = getPageTiles(stores, ownProps.pageIndex);
  const tileBitmaps = pageTiles && pageTiles.map(tile => tile.tileBitmap);

  return {
    ...ownProps,
    onClick: evt => {
      persistEvent(evt);
      pageContentHandler.handleClick(evt, ownProps.pageIndex, stores);
      const { x, y } = getCoordinatesRelativeToPage(evt);
      const pdfPoint = transformViewToPDF(x, y, ownProps.pageIndex, stores);
      overlayEvents.trigger("pageclick", {
        eventObj: evt,
        params: {
          pageIndex: ownProps.pageIndex,
          pdfPoint
        }
      });
      if (!getShouldUseMobileModernViewer(stores)) {
        evt.stopPropagation();
      }
    },
    onMouseOver: evt => {
      triggerPageMouseEnterEvent(evt, ownProps.pageIndex);
    },
    isTileCanvasLoaded: getIsTileCanvasLoaded(stores),
    onMouseLeave: evt => {
      triggerPageMouseLeaveEvent(evt, ownProps.pageIndex);
    },
    setShouldTriggerEnterEvent: getPage(stores, ownProps.pageIndex).setShouldTriggerEnterEvent,
    onMouseDown: evt => {
      pageContentHandler.handleMouseDown(evt, ownProps.pageIndex, stores);
      overlayEvents.trigger("mouseDownOnPage", {
        eventObj: evt,
        params: { pageIndex: ownProps.pageIndex }
      });
      evt.stopPropagation(); // stop bubbling of this event to the document level.
      // IE an Edge on Shift Click selects our UI as well so disableing default in
      // case of shif mouse down.
      if ((isIE() || isEdge()) && evt.shiftKey) {
        evt.preventDefault();
      }
    },
    onMouseDownCapture: evt => {
      pageContentHandler.handleMouseDownCapture(evt, stores);
    },
    onMouseUp: evt => {
      overlayEvents.trigger("mouseUpOnPage", {
        eventObj: evt,
        params: { pageIndex: ownProps.pageIndex },
        isThereAnySelection: isThereAnySelection(stores)
      });
      // setting touch event end on mouse up as well, because in IE press may come but touch end may not.
      if (isTouchEvent(stores) === true) stores.touchSelStore.setTouchEvent();
      pageContentHandler.handleMouseUp(evt, ownProps.pageIndex, stores);
    },
    onMouseMove: evt => {
      pageContentHandler.handleMouseMove(evt.nativeEvent, ownProps.pageIndex, stores);
    },
    onDoubleClick: evt => {
      overlayEvents.trigger("pagedblclick", {
        eventObj: evt,
        params: { pageIndex: ownProps.pageIndex }
      });
    },
    pageIndex: ownProps.pageIndex,
    pageOverlays: getPageOverlays(stores).peek(), // to trigger reactivity when items are pushed
    pageCurrHeight: getPageHeight(stores, ownProps.pageIndex),
    pageCurrWidth: getPageWidth(stores, ownProps.pageIndex),
    pageOrigWidth: getPageOrigWidth(stores, ownProps.pageIndex),
    pageOrigHeight: getPageOrigHeight(stores, ownProps.pageIndex),
    viewRotation: getViewRotationAngle(stores),
    pageRotation: getPageRotation(stores, ownProps.pageIndex),
    isAJSRenditionLoded: isInitialPageLoaded(stores),
    hasHighlights:
      getPageQuadsArray(stores, ownProps.pageIndex) && getPageQuadsArray(stores, ownProps.pageIndex).length > 0,
    hasCursor: getCaretMode(stores) && getIsCursorVisible(stores) && getCursorPageIndex(stores) === ownProps.pageIndex,
    showGrabbers: (function() {
      return showGrabbers(stores, ownProps.pageIndex);
    })(),
    shouldShowInteractiveTooltips: getShouldShowInteractiveTooltips(stores),
    pageZoomLevel: (function() {
      return getPageZoomLevel(stores, ownProps.pageIndex);
    })(),
    handleTouchEnd: () => {
      stores.touchSelStore.setTouchEvent();
      pageContentHandler.getTextManagerInstance().handleTouchEnd();
    },
    handlePress: event => {
      stores.touchSelStore.setTouchEvent(true);
      pageContentHandler.getTextManagerInstance().clearSelection(stores);
      event.cancelable !== false && event.preventDefault();
      pageContentHandler.selectWord(event, stores);
    },
    handleTap: event => {
      const { contentType } = getLatestHitTestResponse(stores);
      if (event && event.srcEvent && event.srcEvent.shiftKey) return;
      if (contentType !== pageContentTypes.IMAGE) pageContentHandler.getTextManagerInstance().clearSelection(stores);
    },
    handleDoubleTap: event => {
      stores.touchSelStore.setTouchEvent();
      pageContentHandler.getTextManagerInstance().clearSelection(stores);
      event.cancelable !== false && event.preventDefault();
      pageContentHandler.selectWord(event, stores);
    },
    handleTouchDrag: event => {
      if (isTouchSelectionInProgress(stores) === true) {
        event.cancelable !== false && event.preventDefault();
        pageContentHandler.handleTouchDrag(event, stores);
      }
    },
    onContextMenu: async event => {
      const { x, y } = getCoordinatesRelativeToPage(event);
      const url = await getHyperlinkUrl(x, y, ownProps.pageIndex);

      if (url && getShouldUseModernViewer(stores)) {
        contextMenuManager.triggerContextMenu(
          event,
          stores,
          {
            pageIndex: ownProps.pageIndex
          },
          contextMenuTriggerType.HYPERLINK_CLICK
        );
      } else {
        contextMenuManager.triggerContextMenu(event, stores, {
          pageIndex: ownProps.pageIndex
        });
      }
    },
    pageLoadStatus: getPageLoadStatus(stores, ownProps.pageIndex),
    getEventOnGrabber: () => getEventOnGrabber(stores),
    setEventOnGrabber: flag => {
      stores.touchSelStore.setEventOnGrabber(flag);
    },
    tileBitmaps,
    cursorStateChanged: getCursorStateChange(stores),
    currentCursor: getCurrentCursor(stores),
    isTextSelectionEnabled: isTextSelectionEnabled(stores),
    isPanActive: isPanActive(stores),
    currentPageIndex: getCurrentPageNumber(stores) - 1,
    shouldSkipMouseEnterLeave: ownProps.shouldSkipMouseEnterLeave,
    shouldRenderReadAloudOverLay: isReadAloudActive(stores) && !isReadAloudPaused(stores),
    shouldRenderHyperlinkOverlay: isHighContrastEnabled(stores),
    boxes: getPageBoxes(stores, ownProps.pageIndex),
    accNodePrefix: getPageAccText(stores, ownProps.pageIndex) ? FORM_ACCESSIBILITY_NODES_PREFIX : null,
    accTextLoadFailed: isPageTextLoadFailed(stores, ownProps.pageIndex),
    overlayUpdatedTimestamp: getOverlayUpdatedTimestamp(stores),
    isFormsAccEnabled: shouldFetchAccContent(stores),
    isFitOneFullPageMode: isFitOneFullPageMode(stores),
    isMultiDocModeEnabled: getIsMultiDocModeEnabled(stores)
  };
}

const PageCanvasContainer = inject(mapStoreToProps)(withTheme(PageCanvas));

export { PageCanvasContainer };
